import { useQuery } from '@tanstack/react-query';
import { X, User, Calendar, Weight, Ruler, UserCircle, AlertCircle } from 'lucide-react';
import { clientApi } from '../services/api';

interface ClientProfileViewModalProps {
  clientId: string;
  onClose: () => void;
}

export function ClientProfileViewModal({ clientId, onClose }: ClientProfileViewModalProps) {
  // Fetch client details
  const { data: client, isLoading: clientLoading } = useQuery({
    queryKey: ['client', clientId],
    queryFn: async () => {
      const response = await clientApi.get(clientId);
      return response.data;
    },
  });

  // Fetch client profile
  const {
    data: profile,
    isLoading: profileLoading,
    error,
  } = useQuery({
    queryKey: ['clientProfile', clientId],
    queryFn: async () => {
      const response = await clientApi.getProfile(clientId);
      return response.data;
    },
    enabled: !!client,
  });

  const isLoading = clientLoading || profileLoading;

  const sexLabels = {
    male: 'Male',
    female: 'Female',
    other: 'Other',
    prefer_not_to_say: 'Prefer not to say',
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Client Profile</h2>
            {client && (
              <p className="text-sm text-gray-600 mt-1">
                {client.first_name} {client.last_name}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <p className="text-gray-500 mt-3">Loading profile...</p>
            </div>
          ) : error ? (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">Cannot access profile</p>
                <p className="text-sm text-red-700 mt-1">
                  You can only view profiles of clients with active plan assignments.
                </p>
              </div>
            </div>
          ) : !profile ? (
            <div className="text-center py-8">
              <UserCircle className="w-16 h-16 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-500">Client has not completed their profile yet</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Profile Header */}
              <div className="flex items-center gap-4 pb-4 border-b">
                {client?.profile_photo_url ? (
                  <img
                    src={client.profile_photo_url}
                    alt={`${client.first_name} ${client.last_name}`}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
                    <UserCircle className="w-10 h-10 text-gray-400" />
                  </div>
                )}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {client?.first_name} {client?.last_name}
                  </h3>
                  <p className="text-sm text-gray-600">{client?.email}</p>
                </div>
              </div>

              {/* Profile Information */}
              <div className="space-y-4">
                {/* Age */}
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-700">Age</p>
                    <p className="text-lg text-gray-900">{profile.age} years old</p>
                  </div>
                </div>

                {/* Sex */}
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-700">Sex</p>
                    <p className="text-lg text-gray-900">{sexLabels[profile.sex]}</p>
                  </div>
                </div>

                {/* Body Type */}
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                    <UserCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-700">Body Type</p>
                    <p className="text-lg text-gray-900 capitalize">{profile.body_type}</p>
                  </div>
                </div>

                {/* Weight */}
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Weight className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-700">Weight</p>
                    <p className="text-lg text-gray-900">
                      {profile.weight} {profile.weight_unit}
                    </p>
                  </div>
                </div>

                {/* Height */}
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <Ruler className="w-5 h-5 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-700">Height</p>
                    <p className="text-lg text-gray-900">
                      {profile.height} {profile.height_unit}
                    </p>
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              <div className="pt-4 border-t">
                <p className="text-xs text-gray-500">
                  Profile information
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
